import { createSelector } from '@reduxjs/toolkit';
import { Store } from 'components/common/types/Store.types';
import {
  buildCampaignPlanLinkInvalidatingFields,
  hasDirectSalesAllocationInvalidatingCampaignChangeFromFields,
  hasDirectSalesCampaignPlanLinkInvalidatingChangeFromFields,
  isCodeNameFieldChanged,
} from './compareDirectSalesCampaignLevelFields';
import { getIsNewDeal } from './getIsNewDeal';

export const getHasDirectSalesAllocationInvalidatingCampaignChange = createSelector(
  getIsNewDeal,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.advertiser?.code,
  (state: Store) => state.dealManagement.commonDeal.advertiser?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.brand?.code,
  (state: Store) => state.dealManagement.commonDeal.brand?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.agency?.code,
  (state: Store) => state.dealManagement.commonDeal.agency?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.specialist?.code,
  (state: Store) => state.dealManagement.commonDeal.specialist?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.productCategory?.code,
  (state: Store) => state.dealManagement.commonDeal.productCategory?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.productCategoryExclusivity,
  (state: Store) => state.dealManagement.commonDeal.productCategoryExclusivity,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.duplicationExclusion,
  (state: Store) => state.dealManagement.commonDeal.duplicationExclusion,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.juxtapositionExclusion,
  (state: Store) => state.dealManagement.commonDeal.juxtapositionExclusion,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.exclusions,
  (state: Store) => state.dealManagement.commonDeal.exclusions,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.advertiserBlockExclusion,
  (state: Store) => state.dealManagement.commonDeal.advertiserBlockExclusion,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.agencyBlockExclusion,
  (state: Store) => state.dealManagement.commonDeal.agencyBlockExclusion,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.specialistBlockExclusion,
  (state: Store) => state.dealManagement.commonDeal.specialistBlockExclusion,
  (
    isNewDeal,
    backupAdvertiserCode,
    currentAdvertiserCode,
    backupBrandCode,
    currentBrandCode,
    backupAgencyCode,
    currentAgencyCode,
    backupSpecialistCode,
    currentSpecialistCode,
    backupProductCategoryCode,
    currentProductCategoryCode,
    backupProductCategoryExclusivity,
    currentProductCategoryExclusivity,
    backupDuplicationExclusion,
    currentDuplicationExclusion,
    backupJuxtapositionExclusion,
    currentJuxtapositionExclusion,
    backupExclusions,
    currentExclusions,
    backupAdvertiserBlockExclusion,
    currentAdvertiserBlockExclusion,
    backupAgencyBlockExclusion,
    currentAgencyBlockExclusion,
    backupSpecialistBlockExclusion,
    currentSpecialistBlockExclusion,
  ) =>
    hasDirectSalesAllocationInvalidatingCampaignChangeFromFields({
      isNewDeal,
      backupAdvertiserCode,
      currentAdvertiserCode,
      backupBrandCode,
      currentBrandCode,
      backupAgencyCode,
      currentAgencyCode,
      backupSpecialistCode,
      currentSpecialistCode,
      backupProductCategoryCode,
      currentProductCategoryCode,
      backupProductCategoryExclusivity,
      currentProductCategoryExclusivity,
      backupDuplicationExclusion,
      currentDuplicationExclusion,
      backupJuxtapositionExclusion,
      currentJuxtapositionExclusion,
      backupExclusions,
      currentExclusions,
      backupAdvertiserBlockExclusion,
      currentAdvertiserBlockExclusion,
      backupAgencyBlockExclusion,
      currentAgencyBlockExclusion,
      backupSpecialistBlockExclusion,
      currentSpecialistBlockExclusion,
    }),
);

export const getHasDirectSalesCampaignPlanLinkInvalidatingChange = createSelector(
  getIsNewDeal,
  (state: Store) => state.dealManagement.backupFormData.commonDeal,
  (state: Store) => state.dealManagement.commonDeal,
  (isNewDeal, backupCommonDeal, commonDeal) =>
    hasDirectSalesCampaignPlanLinkInvalidatingChangeFromFields(
      buildCampaignPlanLinkInvalidatingFields(isNewDeal, backupCommonDeal, commonDeal),
    ),
);

export const getHasDirectSalesSalesAssignmentFieldsChanged = createSelector(
  getIsNewDeal,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.salesPerson?.code,
  (state: Store) => state.dealManagement.commonDeal.salesPerson?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.salesTeam?.code,
  (state: Store) => state.dealManagement.commonDeal.salesTeam?.code,
  (state: Store) => state.dealManagement.backupFormData.commonDeal.adminPerson?.code,
  (state: Store) => state.dealManagement.commonDeal.adminPerson?.code,
  (
    isNewDeal,
    backupSalesPersonCode,
    currentSalesPersonCode,
    backupSalesTeamCode,
    currentSalesTeamCode,
    backupAdminPersonCode,
    currentAdminPersonCode,
  ) => {
    if (isNewDeal) return false;

    return (
      isCodeNameFieldChanged(backupSalesPersonCode, currentSalesPersonCode) ||
      isCodeNameFieldChanged(backupSalesTeamCode, currentSalesTeamCode) ||
      isCodeNameFieldChanged(backupAdminPersonCode, currentAdminPersonCode)
    );
  },
);

export const getHasDirectSalesCampaignLevelChanged = createSelector(
  getHasDirectSalesAllocationInvalidatingCampaignChange,
  getHasDirectSalesSalesAssignmentFieldsChanged,
  (hasAllocationInvalidatingCampaignChange, hasSalesAssignmentFieldsChanged) =>
    hasAllocationInvalidatingCampaignChange || hasSalesAssignmentFieldsChanged,
);
